/**
 * ПОЛНЫЙ ПУТЬ: D:\QR\QR-WEB\dashboard\src\components\Admin\AdminTransactions\AdminTransactionsFailed.js
 * ВЕРСИЯ: 1.0.0
 * ВРЕМЯ СОЗДАНИЯ: 15.11.2025 18:20
 * 
 * ОПИСАНИЕ: Компонент управления ошибочными/отклонёнными транзакциями
 * - Список всех транзакций со статусом "ошибка"
 * - Типы ошибок и коды
 * - Возможность повторить транзакцию
 * - Просмотр логов ошибок
 * - Анализ проблем
 * - Экспорт в CSV
 */

import React, { useState, useEffect } from 'react';
import './AdminTransactionsFailed.css';

const AdminTransactionsFailed = () => {
  const [transactions, setTransactions] = useState([]);
  const [filteredTransactions, setFilteredTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [errorTypeFilter, setErrorTypeFilter] = useState('all');
  const [selectedTx, setSelectedTx] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    fetchFailedTransactions();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [transactions, searchQuery, errorTypeFilter]);

  const fetchFailedTransactions = async () => {
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem('authToken');
      const response = await fetch(
        'https://qr-money.onrender.com/api/admin/transactions?status=failed',
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      const txList = data.data || data.transactions || data || [];
      const failed = Array.isArray(txList)
        ? txList.filter(t => t.status === 'failed')
        : [];

      setTransactions(failed);
      console.log('✅ Ошибочные транзакции загружены:', failed);
    } catch (err) {
      console.error('❌ Ошибка при загрузке ошибочных транзакций:', err);
      setError(err.message);
      setTransactions(getMockFailedTransactions());
    } finally {
      setLoading(false);
    }
  };

  const getMockFailedTransactions = () => [
    {
      id: 'TXN-2025-11-15-001',
      type: 'transfer',
      from: 'alice@test.com',
      to: 'blocked-user@test.com',
      amount: 150,
      status: 'failed',
      error_type: 'user_blocked',
      error_code: 'ERR_USER_BLOCKED',
      error_message: 'Получатель заблокирован',
      created_at: '2025-11-14T15:30:00Z',
      failed_at: '2025-11-14T15:30:05Z',
      retry_count: 0
    },
    {
      id: 'TXN-2025-11-15-002',
      type: 'qr_claim',
      from: 'System',
      to: 'alice@test.com',
      amount: 50,
      status: 'failed',
      error_type: 'insufficient_balance',
      error_code: 'ERR_INSUFFICIENT_BALANCE',
      error_message: 'Недостаточно средств на кошельке системы',
      created_at: '2025-11-14T14:20:00Z',
      failed_at: '2025-11-14T14:20:03Z',
      retry_count: 2
    },
    {
      id: 'TXN-2025-11-15-003',
      type: 'payment',
      from: 'business@test.com',
      to: 'System',
      amount: 500,
      status: 'failed',
      error_type: 'network_error',
      error_code: 'ERR_BLOCKCHAIN_TIMEOUT',
      error_message: 'Таймаут подключения к блокчейну',
      created_at: '2025-11-14T12:00:00Z',
      failed_at: '2025-11-14T12:00:30Z',
      retry_count: 1
    },
    {
      id: 'TXN-2025-11-14-001',
      type: 'transfer',
      from: 'user1@test.com',
      to: 'user2@test.com',
      amount: 100,
      status: 'failed',
      error_type: 'invalid_address',
      error_code: 'ERR_INVALID_ADDRESS',
      error_message: 'Адрес кошелька некорректен',
      created_at: '2025-11-13T16:45:00Z',
      failed_at: '2025-11-13T16:45:02Z',
      retry_count: 0
    }
  ];

  const applyFilters = () => {
    let filtered = transactions;

    if (errorTypeFilter !== 'all') {
      filtered = filtered.filter(t => t.error_type === errorTypeFilter);
    }

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(t =>
        t.id.toLowerCase().includes(query) ||
        t.from.toLowerCase().includes(query) ||
        t.to.toLowerCase().includes(query)
      );
    }

    setFilteredTransactions(filtered);
  };

  const handleRetryTransaction = async () => {
    if (!selectedTx) return;

    if (!window.confirm(`Повторить транзакцию ${selectedTx.id}?`)) {
      return;
    }

    try {
      setActionLoading(true);
      const token = localStorage.getItem('authToken');

      const response = await fetch(
        `https://qr-money.onrender.com/api/admin/transactions/${selectedTx.id}/retry`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (response.ok) {
        setTransactions(transactions.filter(t => t.id !== selectedTx.id));
        setSelectedTx(null);
        alert('✅ Транзакция переотправлена');
      } else {
        const errData = await response.json();
        alert(`❌ Ошибка: ${errData.message || 'при повторе'}`);
      }
    } catch (err) {
      console.error('❌ Ошибка:', err);
      alert('❌ Ошибка при повторе транзакции');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteTransaction = async () => {
    if (!selectedTx) return;

    if (!window.confirm(`Удалить транзакцию ${selectedTx.id}? Это действие необратимо.`)) {
      return;
    }

    try {
      setActionLoading(true);
      const token = localStorage.getItem('authToken');

      const response = await fetch(
        `https://qr-money.onrender.com/api/admin/transactions/${selectedTx.id}`,
        {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (response.ok) {
        setTransactions(transactions.filter(t => t.id !== selectedTx.id));
        setSelectedTx(null);
        alert('✅ Транзакция удалена');
      } else {
        const errData = await response.json();
        alert(`❌ Ошибка: ${errData.message || 'при удалении'}`);
      }
    } catch (err) {
      console.error('❌ Ошибка:', err);
      alert('❌ Ошибка при удалении транзакции');
    } finally {
      setActionLoading(false);
    }
  };

  const handleExportCSV = () => {
    const csv = [
      ['ID', 'Тип', 'От', 'Кому', 'Сумма', 'Ошибка', 'Дата', 'Повторов'].join(','),
      ...filteredTransactions.map(t =>
        [
          t.id,
          t.type,
          t.from,
          t.to,
          t.amount,
          t.error_message,
          new Date(t.created_at).toLocaleDateString('ru-RU'),
          t.retry_count
        ].join(',')
      )
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `failed_transactions_${Date.now()}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const getErrorColor = (errorType) => {
    const colors = {
      user_blocked: 'error-blocked',
      insufficient_balance: 'error-balance',
      network_error: 'error-network',
      invalid_address: 'error-invalid',
      timeout: 'error-timeout'
    };
    return colors[errorType] || 'error-other';
  };

  const getErrorLabel = (errorType) => {
    const labels = {
      user_blocked: '🚫 Пользователь заблокирован',
      insufficient_balance: '💔 Недостаточно средств',
      network_error: '🌐 Ошибка сети',
      invalid_address: '❌ Некорректный адрес',
      timeout: '⏰ Таймаут'
    };
    return labels[errorType] || errorType;
  };

  const getTimeSinceFail = (failedAt) => {
    const failed = new Date(failedAt);
    const now = new Date();
    const diffMinutes = Math.floor((now - failed) / 60000);

    if (diffMinutes < 60) return `${diffMinutes} мин назад`;
    if (diffMinutes < 1440) return `${Math.floor(diffMinutes / 60)} ч назад`;
    return `${Math.floor(diffMinutes / 1440)} дн назад`;
  };

  if (loading) {
    return (
      <div className="admin-failed-page">
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <p>⏳ Загрузка ошибочных транзакций...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-failed-page">
      {/* ЗАГОЛОВОК */}
      <div className="failed-header">
        <h1>❌ Ошибочные транзакции</h1>
        <p>Всего ошибок: {transactions.length} | Показано: {filteredTransactions.length}</p>
      </div>

      {/* ОШИБКА */}
      {error && (
        <div className="error-message">
          ⚠️ {error} (используются тестовые данные)
        </div>
      )}

      {/* КОНТРОЛЫ */}
      <div className="failed-controls">
        <div className="controls-left">
          <input
            type="text"
            className="search-input"
            placeholder="🔍 Поиск по ID, email..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />

          <select
            className="filter-select"
            value={errorTypeFilter}
            onChange={(e) => setErrorTypeFilter(e.target.value)}
          >
            <option value="all">📊 Все типы ошибок</option>
            <option value="user_blocked">🚫 Пользователь заблокирован</option>
            <option value="insufficient_balance">💔 Недостаточно средств</option>
            <option value="network_error">🌐 Ошибка сети</option>
            <option value="invalid_address">❌ Некорректный адрес</option>
            <option value="timeout">⏰ Таймаут</option>
          </select>
        </div>

        <div className="controls-right">
          <button
            className="btn-export"
            onClick={handleExportCSV}
            disabled={filteredTransactions.length === 0}
          >
            📥 Экспорт CSV
          </button>
          <button
            className="btn-refresh"
            onClick={fetchFailedTransactions}
            title="Обновить"
          >
            🔄
          </button>
        </div>
      </div>

      {/* ОСНОВНОЙ КОНТЕНТ */}
      <div className="failed-container">
        {/* ТАБЛИЦА */}
        <div className="failed-table">
          {filteredTransactions.length > 0 ? (
            <table>
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Тип</th>
                  <th>От</th>
                  <th>Кому</th>
                  <th>Сумма</th>
                  <th>Ошибка</th>
                  <th>Дата</th>
                  <th>Повторов</th>
                  <th>Действие</th>
                </tr>
              </thead>
              <tbody>
                {filteredTransactions.map((tx) => (
                  <tr
                    key={tx.id}
                    className={selectedTx?.id === tx.id ? 'active' : ''}
                    onClick={() => setSelectedTx(tx)}
                  >
                    <td className="tx-id">{tx.id}</td>
                    <td className="tx-type">{tx.type}</td>
                    <td className="tx-email">{tx.from}</td>
                    <td className="tx-email">{tx.to}</td>
                    <td className="tx-amount">{tx.amount} DAR</td>
                    <td>
                      <span className={`error-badge ${getErrorColor(tx.error_type)}`}>
                        {getErrorLabel(tx.error_type)}
                      </span>
                    </td>
                    <td className="tx-date">
                      {new Date(tx.created_at).toLocaleDateString('ru-RU')}
                    </td>
                    <td className="tx-retry">
                      <span className="retry-badge">{tx.retry_count}</span>
                    </td>
                    <td>
                      <button
                        className="btn-view"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedTx(tx);
                        }}
                        title="Просмотр"
                      >
                        👁️
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="no-transactions">
              <p>✅ Нет ошибочных транзакций!</p>
            </div>
          )}
        </div>

        {/* ДЕТАЛИ */}
        {selectedTx && (
          <div className="failed-details">
            <div className="details-header">
              <h2>📋 Анализ ошибки</h2>
              <button
                className="btn-close"
                onClick={() => setSelectedTx(null)}
                title="Закрыть"
              >
                ✕
              </button>
            </div>

            <div className="details-content">
              <div className="detail-section error-info">
                <h3>🚨 Информация об ошибке</h3>
                
                <div className="error-type-large">
                  {getErrorLabel(selectedTx.error_type)}
                </div>

                <div className="detail-item">
                  <span className="detail-label">Код ошибки</span>
                  <span className="detail-value mono">{selectedTx.error_code}</span>
                </div>

                <div className="detail-item">
                  <span className="detail-label">Сообщение</span>
                  <span className="detail-value">{selectedTx.error_message}</span>
                </div>
              </div>

              <div className="detail-section">
                <h3>📋 Основная информация</h3>
                
                <div className="detail-item">
                  <span className="detail-label">ID транзакции</span>
                  <span className="detail-value mono">{selectedTx.id}</span>
                </div>

                <div className="detail-item">
                  <span className="detail-label">Тип</span>
                  <span className="detail-value">{selectedTx.type}</span>
                </div>

                <div className="detail-item">
                  <span className="detail-label">От</span>
                  <span className="detail-value">{selectedTx.from}</span>
                </div>

                <div className="detail-item">
                  <span className="detail-label">Кому</span>
                  <span className="detail-value">{selectedTx.to}</span>
                </div>
              </div>

              <div className="detail-section">
                <h3>💰 Сумма</h3>
                
                <div className="detail-item">
                  <span className="detail-label">Сумма</span>
                  <span className="detail-value amount">{selectedTx.amount} DAR</span>
                </div>
              </div>

              <div className="detail-section">
                <h3>🕐 Времени</h3>
                
                <div className="detail-item">
                  <span className="detail-label">Создана</span>
                  <span className="detail-value">
                    {new Date(selectedTx.created_at).toLocaleString('ru-RU')}
                  </span>
                </div>

                <div className="detail-item">
                  <span className="detail-label">Ошибка</span>
                  <span className="detail-value">
                    {new Date(selectedTx.failed_at).toLocaleString('ru-RU')}
                  </span>
                </div>

                <div className="detail-item">
                  <span className="detail-label">Прошло</span>
                  <span className="detail-value">{getTimeSinceFail(selectedTx.failed_at)}</span>
                </div>
              </div>

              <div className="detail-section">
                <h3>🔄 Повторные попытки</h3>
                
                <div className="detail-item">
                  <span className="detail-label">Попыток</span>
                  <span className="detail-value retry-count">
                    {selectedTx.retry_count}
                  </span>
                </div>
              </div>

              {/* ДЕЙСТВИЯ */}
              <div className="action-buttons">
                <button
                  className="btn btn-retry"
                  onClick={handleRetryTransaction}
                  disabled={actionLoading}
                >
                  🔄 {actionLoading ? 'Повтор...' : 'Повторить'}
                </button>
                <button
                  className="btn btn-delete"
                  onClick={handleDeleteTransaction}
                  disabled={actionLoading}
                >
                  🗑️ {actionLoading ? 'Удаление...' : 'Удалить'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminTransactionsFailed;
